import { CustomListKey, ValidationOperation } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { NumberInput } from "components/shared/number-input";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import {
    LayoutSectionKey,
    LayoutSectionParentKey,
    useGetEntitiesQuery,
    useGetLayoutSectionsQuery,
} from "generated/graphql";
import React from "react";
import { useCrmMode, useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { stableQueryOptions } from "util/requests";
import { CustomListEditor } from "./custom-list-editor";
import { EntitySettings } from "./customize/entity-settings";
import { ValidationRuleSetEditor } from "./validation-rule-set-editor";

const HomeCareCustomFieldsContent: React.FC = () => {
    const entitiesQuery = useGetEntitiesQuery({}, stableQueryOptions());
    const detailsQuery = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.HomeCareLeadDetails },
        stableQueryOptions(),
    );
    const intakeColumn1Query = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.HomeCareLeadIntakeColumn1 },
        stableQueryOptions(),
    );
    const intakeColumn2Query = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.HomeCareLeadIntakeColumn2 },
        stableQueryOptions(),
    );

    if (
        detailsQuery.isLoading ||
        intakeColumn1Query.isLoading ||
        intakeColumn2Query.isLoading
    ) {
        return <Spinner />;
    }

    if (detailsQuery.isError) {
        return (
            <InlineBanner type="error">
                Could not load home care layout. Try refreshing the page.
            </InlineBanner>
        );
    }

    const hasAdditionalInformationSection =
        detailsQuery.data?.getLayoutSections?.layoutSections?.some(
            s => s.sectionKey === LayoutSectionKey.HomeCareLeadAdditionalInformation,
        );
    const hasIntakeColumns =
        (intakeColumn1Query.data?.getLayoutSections?.layoutSections?.length ?? 0) > 0 &&
        (intakeColumn2Query.data?.getLayoutSections?.layoutSections?.length ?? 0) > 0;

    if (!hasAdditionalInformationSection || !hasIntakeColumns) {
        return (
            <InlineBanner type="info">
                Home care layout is still being set up for this agency. An administrator
                can run Ensure Home Care Setup from Tenant Settings to finish. Contact
                support if this persists.
            </InlineBanner>
        );
    }

    return (
        <QueryRenderer
            query={entitiesQuery}
            name="AgencySettingsHomeCareClients.getEntities">
            {data => (
                <EntitySettings entityName="HomeCareLead" entities={data.getEntities} />
            )}
        </QueryRenderer>
    );
};

export const AgencySettingsHomeCareClients: React.FC<unknown> = () => {
    const homeCareEnabled = useFeature("homeCarePrototype");
    const wellSkyEnabled = useFeature("wellSkyIntegration");
    const { mode } = useCrmMode();
    const { settings, putSetting } = useTenantSettings();

    if (!homeCareEnabled) {
        return null;
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Home Care Clients</ContentHeader>
                <ContentSubheader>
                    Closed clients stay on the pipeline for this many days after entering
                    a closed status.
                    {mode === "combined"
                        ? " This is the same setting that controls closed placement clients."
                        : null}
                </ContentSubheader>
                <NumberInput
                    label="Number of days to show closed clients"
                    initial={settings.closedPaidDuration}
                    onCommit={d => putSetting("closedPaidDuration", d)}
                />
            </ContentFull>
            <ContentFull>
                <ContentHeader>Home Care Client Statuses</ContentHeader>
                <ContentSubheader>
                    Customize home care client status labels, order, and categories to
                    support your home care workflow.
                </ContentSubheader>
                <CustomListEditor customListKey={CustomListKey.HomeCareLeadStatus} />
            </ContentFull>
            <ContentFull>
                <ContentHeader>Customize Home Care Client Layout</ContentHeader>
                <ContentSubheader>
                    Configure the sections and fields that appear on every home care
                    client&apos;s overview and intake tabs, and add your own custom fields
                    and sections.
                </ContentSubheader>
                <HomeCareCustomFieldsContent />
            </ContentFull>
            <ContentFull>
                <ContentHeader>Validation Rules</ContentHeader>
                <ContentSubheader>
                    Require fields to be filled in before certain actions can be taken on
                    a lead. Hard rules block the action; warning rules let the sender
                    confirm and proceed.
                </ContentSubheader>
                <div className="space-y-8">
                    {wellSkyEnabled && (
                        <ValidationRuleSetEditor
                            operation={ValidationOperation.SendToWellSky}
                            title="Sending to WellSky"
                            description="These fields are checked before a lead is sent to WellSky Personal Care."
                        />
                    )}
                    <ValidationRuleSetEditor
                        operation={ValidationOperation.TransferClient}
                        title="Transferring a client"
                        description="These fields are checked before a client is transferred to another agency."
                    />
                </div>
            </ContentFull>
        </div>
    );
};
